import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";

import {
  fetchOutletUser,
  OutletUser,
} from "../../app/services/OutletUserService";
import {
  fetchOrganisation,
  Organisation,
} from "../../app/services/OrganisationService";
import { fetchOutlet, Outlet } from "../../app/services/OutletService";
import PageWrapper from "../../app/shared/common/PageWrapper";
import CreateTable from "../../app/shared/common/table/CreateTable";
import SearchBox from "../../app/shared/common/SearchBox";
import DropDown from "../../app/shared/common/DropDown";
import TableActions from "../../app/shared/common/table/TableAction";
const tableColumns = [
  { key: "user.name", label: "Name" },
  { key: "user.email", label: "Email" },
  { key: "org.name", label: "Organisation" },
  { key: "outlet.name", label: "Outlet" },
  { key: "user.role.name", label: "Role" },
];
const UserList = () => {
  const [users, setUsers] = useState<OutletUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<OutletUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState("");

  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [organisationId, setOrganisationId] = useState<string>("");

  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [outletId, setOutletId] = useState<string>("");

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await fetchOutletUser();
      if (res.status === "success") {
        setUsers(res.data);
        setFilteredUsers(
          filterRows(res.data, searchText, organisationId, outletId)
        );
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error("Failed to load outlet user.");
    } finally {
      setLoading(false);
    }
  };

  const loadOrganisations = async () => {
    try {
      const res = await fetchOrganisation();
      if (res.status === "success") {
        setOrganisations(res.data);
      }
    } catch (err) {
      toast.error("Failed to load organisations.");
    }
  };

  const loadOutlets = async () => {
    try {
      const res = await fetchOutlet();
      if (res.status === "success") {
        setOutlets(res.data);
      }
    } catch (err) {
      toast.error("Failed to load outlets.");
    }
  };

  const filterRows = (
    rows: OutletUser[],
    query: string,
    organisationId: string,
    outletId: string
  ) => {
    return rows.filter((row) => {
      const matchesSearch = query
        ? Object.values(row).some((value) =>
            typeof value === "object"
              ? JSON.stringify(value)
                  .toLowerCase()
                  .includes(query.toLowerCase())
              : value?.toString().toLowerCase().includes(query.toLowerCase())
          )
        : true;

      const matchesOrg = organisationId
        ? String(row.org_id) === String(organisationId)
        : true;

      const matchesOutlet = outletId
        ? String(row.outlet_id) === String(outletId)
        : true;

      return matchesSearch && matchesOrg && matchesOutlet;
    });
  };

  useEffect(() => {
    loadUsers();
    loadOrganisations();
    loadOutlets();
  }, []);

  useEffect(() => {
    setFilteredUsers(filterRows(users, searchText, organisationId, outletId));
  }, [searchText, organisationId, outletId, users]);

  return (
    <PageWrapper
      title={"Users"}
      navigateTo={"/organisation/outlet/user/add"}
      buttonTitle={"Add Outlet User"}
    >
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <SearchBox value={searchText} onChange={setSearchText} />
        <DropDown
          label="Filter by organisation"
          value={organisationId}
          onChange={setOrganisationId}
          options={organisations}
          allLabel="All Organisation"
        />

        <DropDown
          label="Filter by Outlet"
          value={outletId}
          onChange={setOutletId}
          options={outlets}
          allLabel="All Outlet"
        />
      </Box>

      <CreateTable
        data={filteredUsers}
        columns={tableColumns}
        loading={loading}
        renderActions={(row) => (
          <TableActions
            navigateTo={`/organisation/outlet/user/update/${row.user?.id}`}
            // onClick={() => handleOpenDialog(row.id)}
          />
        )}
      />
    </PageWrapper>
  );
};

export default UserList;
